import { Button, Group, LoadingOverlay, Modal, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import React, { useEffect } from 'react'
import styles from '../../styles/admin/ChapterList.module.scss'

import { useChaptersUpdate } from '../../api/hooks/Chapters/useChaptersUpdate'
import { ChapterItem, ChaptersUpdateData } from '../../types/chapters'
import SaveButton from '../common/SaveButton'
import BaseButton from '../common/BaseButton'

interface ChapterEditModalProps {
  opened: boolean
  onClose: () => void
  item: ChapterItem | null
}

interface FormValues {
  MAIN: string
  SUB: string
  UNIT: string
  DETAIL_UNIT: string
}

const ChapterEditModal: React.FC<ChapterEditModalProps> = ({
  opened,
  onClose,
  item,
}) => {
  const { mutateAsync: updateChapter, isPending } = useChaptersUpdate()

  const form = useForm<FormValues>({
    initialValues: {
      MAIN: '',
      SUB: '',
      UNIT: '',
      DETAIL_UNIT: '',
    },
  })

  useEffect(() => {
    if (item && opened) {
      form.setValues({
        MAIN: item.MAIN || '',
        SUB: item.SUB || '',
        UNIT: item.UNIT || '',
        DETAIL_UNIT: item.DETAIL_UNIT || '',
      })
    }
  }, [item, opened])

  const handleSubmit = async (values: FormValues) => {
    if (!item) {
      notifications.show({
        title: 'Error',
        message: 'No item selected for update',
        color: 'red',
      })
      return
    }

    try {
      const updateData: ChaptersUpdateData = {
        LEVEL_ID: item.LEVEL_ID,
        ...values,
      }

      await updateChapter(updateData)

      notifications.show({
        title: 'Success',
        message: 'Chapter updated successfully',
        color: 'green',
      })

      onClose()
    } catch (error) {
      notifications.show({
        title: 'Error',
        message:
          error instanceof Error ? error.message : 'Failed to update chapter',
        color: 'red',
      })
    }
  }

  const handleClose = () => {
    if (!isPending) {
      form.reset()
      onClose()
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Edit Chapter - ${item?.LEVEL || ''}`}
      size="lg"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
      styles={{
        content: {
          borderRadius: '15px',
        },
        close: {
          top: '-30px',
          right: '20px',
        },
      }}
    >
      <div style={{ position: 'relative', padding: '20px' }}>
        <LoadingOverlay visible={isPending} />

        {item && (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <TextInput
              label="Main Chapter"
              placeholder="Enter main chapter"
              disabled={isPending}
              {...form.getInputProps('MAIN')}
              mb="md"
              styles={{
                label: {
                  marginBottom: '8px',
                },
              }}
            />

            <TextInput
              label="Sub Chapter"
              placeholder="Enter sub chapter"
              disabled={isPending}
              {...form.getInputProps('SUB')}
              mb="md"
              styles={{
                label: {
                  marginBottom: '8px',
                },
              }}
            />

            <TextInput
              label="Unit"
              placeholder="Enter unit"
              disabled={isPending}
              {...form.getInputProps('UNIT')}
              mb="md"
              styles={{
                label: {
                  marginBottom: '8px',
                },
              }}
            />

            <TextInput
              label="Detail Unit"
              placeholder="Enter detail unit"
              disabled={isPending}
              {...form.getInputProps('DETAIL_UNIT')}
              mb="md"
              styles={{
                label: {
                  marginBottom: '8px',
                },
              }}
            />

            <Group justify="flex-end" mt="xl">
              <BaseButton type="submit" className={styles.addChapterButton}>
                Update Chapter
              </BaseButton>
              <BaseButton
                onClick={handleClose}
                disabled={isPending}
                className={styles.cancelButton}
              >
                Cancel
              </BaseButton>
            </Group>
          </form>
        )}
      </div>
    </Modal>
  )
}

export default ChapterEditModal
